import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import QRCode from 'react-qr-code';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  Camera, Search, X, Check, CreditCard, Banknote,
  Phone, MapPin, Tv, Calendar, Tag, ScanText,
  ShieldCheck, ShieldAlert, KeyRound, SkipForward, Edit2
} from 'lucide-react';
import { customerAPI, paymentAPI, configAPI, kycAPI, catvAPI } from '../services/api';

const SCANNER_ID = 'kyc-stb-scanner';

function normalizeNUID(raw) {
  const s = raw.trim();
  if (/^\d{12}$/.test(s)) {
    const prefix = parseInt(s.substring(0, 2), 10);
    if (prefix >= 29 && prefix <= 40) return s.substring(0, 10);
  }
  if (/^(832[bB]|8317)/i.test(s)) return '0000' + s;
  return s;
}

function extractNUIDCandidates(text) {
  const candidates = new Set();
  (text.match(/\b\d{8,12}\b/g) || []).forEach(n => candidates.add(n));
  (text.match(/\b(832[bB]|8317)[0-9a-fA-F]{4,8}\b/g) || []).forEach(h => candidates.add(h));
  return [...candidates];
}

const ID_TYPES = [
  { value: 'aadhar', label: 'Aadhar Card' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'voter_id', label: 'Voter ID' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'passport', label: 'Passport' },
];

export default function KYCPage() {
  const navigate = useNavigate();
  const [step, setStep]       = useState('scan'); // scan | kyc | payment
  const [scanMode, setScanMode] = useState('idle'); // idle | camera | manual | ocr | ocrResults
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);

  // Scan state
  const [manualSerial, setManual] = useState('');
  const [ocrCandidates, setOcrCandidates] = useState([]);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const scannerRef = useRef(null);
  const ocrInputRef = useRef(null);

  // Packages list
  const [catvPackages, setCatvPackages] = useState([]);
  useEffect(() => {
    catvAPI.getPackages().then(r => setCatvPackages(r.data.data || [])).catch(() => {});
  }, []);

  // KYC form
  const [kycForm, setKycForm] = useState({
    name: '', mobile: '', address: '', area: '', city: '',
    idProofType: '', idProofNumber: '',
    catvPackageId: '', catvExpiryDate: '', customAmount: '',
  });
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [devOtp, setDevOtp] = useState(null); // shown when SMS not configured
  const [kycSaving, setKycSaving] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [showSkip, setShowSkip] = useState(false);

  // Payment state
  const [payMode, setPayMode] = useState(null);
  const [baseAmount, setBaseAmount] = useState('');
  const [selectedUpi, setSelUpi] = useState(1);
  const [paying, setPaying] = useState(false);
  const [config, setConfig] = useState({ upiId: '', upiId2: '', upiName: 'JagdishNet', upiDiscountPercent: 5 });

  useEffect(() => {
    configAPI.get().then(r => { if (r.data?.data) setConfig(r.data.data); }).catch(() => {});
  }, []);

  const discountPct = config.upiDiscountPercent || 0;
  const cashAmount  = parseFloat(baseAmount) || 0;
  const upiAmount   = cashAmount > 0 ? Math.round(cashAmount * (1 - discountPct / 100)) : 0;
  const activeUpiId = selectedUpi === 1 ? config.upiId : config.upiId2;
  const makeUpiLink = (upiId, amt, ref) =>
    `upi://pay?pa=${upiId}&pn=${encodeURIComponent(config.upiName)}&am=${amt}&cu=INR&tn=${encodeURIComponent(`CATV Renewal - ${ref}`)}`;

  // ── Scanner ───────────────────────────────────────────────────────────────
  const startCamera = async () => {
    setScanMode('camera');
    await new Promise(r => setTimeout(r, 100));
    try {
      const scanner = new Html5Qrcode(SCANNER_ID);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 280, height: 120 } },
        (decoded) => { stopCamera(); lookupSerial(decoded.trim()); },
        () => {}
      );
    } catch {
      toast.error('Camera unavailable — use manual entry');
      setScanMode('manual');
    }
  };

  const stopCamera = async () => {
    try { if (scannerRef.current) { await scannerRef.current.stop(); scannerRef.current = null; } } catch {}
  };
  useEffect(() => () => { stopCamera(); }, []);

  const handleOcrCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setOcrProcessing(true);
    setScanMode('ocr');
    try {
      const { createWorker } = await import('tesseract.js');
      const worker = await createWorker('eng', 1, { logger: () => {} });
      await worker.setParameters({ tessedit_char_whitelist: '0123456789abcdefABCDEF ' });
      const { data: { text } } = await worker.recognize(file);
      await worker.terminate();
      const candidates = extractNUIDCandidates(text);
      if (candidates.length === 0) {
        toast.error('No numbers found — try manual entry');
        setScanMode('manual');
      } else {
        setOcrCandidates(candidates);
        setScanMode('ocrResults');
      }
    } catch {
      toast.error('OCR failed — use manual entry');
      setScanMode('manual');
    } finally {
      setOcrProcessing(false);
    }
  };

  const lookupSerial = async (raw) => {
    if (!raw) return;
    const norm = normalizeNUID(raw);
    setLoading(true);
    setScanMode('idle');
    try {
      const res = await customerAPI.getByStb(norm);
      let c = res.data.data;

      // Auto-assign PowerPlus + 1st of next month if CATV customer has no package
      if ((c.customerType === 'catv' || c.customerType === 'both') && !c.catv?.package) {
        try {
          const defRes = await kycAPI.assignDefault(c._id);
          c = defRes.data.data;
          toast.success(defRes.data.message);
        } catch { /* non-fatal — continue with original data */ }
      }

      setCustomer(c);
      const price = c.catv?.package?.price || c.broadband?.plan?.price || '';
      setKycForm({
        name: c.name || '',
        mobile: c.mobile || '',
        address: c.address?.line1 || '',
        area: c.address?.area || '',
        city: c.address?.city || '',
        idProofType: c.idProofType || '',
        idProofNumber: c.idProofNumber || '',
        catvPackageId: c.catv?.package?._id || c.catv?.package || '',
        catvExpiryDate: c.catv?.expiryDate ? new Date(c.catv.expiryDate).toISOString().slice(0, 10) : '',
        customAmount: String(price),
      });
      setBaseAmount(String(price));
      setStep('kyc');
    } catch (err) {
      if (err.response?.status === 404) {
        toast('New STB — redirecting to add customer...', { icon: '📋' });
        navigate(`/customers/add?stb=${encodeURIComponent(norm)}`);
      } else {
        toast.error('Customer not found');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetScan = () => {
    stopCamera();
    setScanMode('idle');
    setManual('');
    setOcrCandidates([]);
  };

  // ── OTP ───────────────────────────────────────────────────────────────────
  const sendOTP = async () => {
    if (!kycForm.mobile || kycForm.mobile.replace(/\D/g, '').length < 10) {
      toast.error('Enter a valid 10-digit mobile number first');
      return;
    }
    setOtpLoading(true);
    try {
      const res = await kycAPI.sendOTP(customer._id, kycForm.mobile);
      setOtpSent(true);
      setDevOtp(res.data.dev ? res.data.otp : null);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otpValue || otpValue.length < 4) { toast.error('Enter the OTP'); return; }
    setOtpLoading(true);
    try {
      await kycAPI.verifyOTP(customer._id, otpValue);
      setOtpVerified(true);
      toast.success('Mobile verified!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Wrong OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Complete KYC ──────────────────────────────────────────────────────────
  const completeKYC = async (skip = false) => {
    setKycSaving(true);
    try {
      await kycAPI.complete(customer._id, {
        name: kycForm.name,
        mobile: kycForm.mobile,
        address: { line1: kycForm.address, area: kycForm.area, city: kycForm.city },
        idProofType: kycForm.idProofType,
        idProofNumber: kycForm.idProofNumber,
        catvPackageId: kycForm.catvPackageId || undefined,
        catvExpiryDate: kycForm.catvExpiryDate || undefined,
        skipOTP: skip,
        skipReason: skip ? (skipReason || 'No mobile / not available') : undefined,
      });
      // Use custom amount for payment if entered
      if (kycForm.customAmount) setBaseAmount(kycForm.customAmount);
      toast.success('KYC completed!');
      setStep('payment');
    } catch (err) {
      toast.error(err.response?.data?.message || 'KYC save failed');
    } finally {
      setKycSaving(false);
    }
  };

  // ── Payment ───────────────────────────────────────────────────────────────
  const collectPayment = async (method) => {
    const amt = method === 'upi' ? upiAmount : cashAmount;
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setPaying(true);
    try {
      await paymentAPI.create({
        customer: customer._id, amount: amt, paymentMethod: method,
        paymentFor: (customer.customerType === 'catv' || customer.customerType === 'both')
          ? 'catv_renewal' : 'broadband_renewal',
      });
      toast.success(`₹${amt} ${method.toUpperCase()} recorded!`);
      setPayMode(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  const reset = () => {
    stopCamera();
    setStep('scan'); setScanMode('idle'); setCustomer(null); setManual('');
    setKycForm({ name:'', mobile:'', address:'', area:'', city:'', idProofType:'', idProofNumber:'' });
    setOtpSent(false); setOtpValue(''); setOtpVerified(false); setDevOtp(null);
    setPayMode(null); setBaseAmount(''); setShowSkip(false); setSkipReason('');
    setOcrCandidates([]);
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 500, margin: '0 auto', padding: '16px 12px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <ShieldCheck size={22} color="var(--primary)" />
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Customer KYC & Collection</h2>
        {(step !== 'scan' || scanMode !== 'idle') && (
          <button onClick={reset} style={iconBtn}><X size={20} /></button>
        )}
      </div>

      {/* Progress */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
        {[['scan','Find Customer'],['kyc','Verify KYC'],['payment','Collect Payment']].map(([s, label], i) => (
          <div key={s} style={{
            flex: 1, padding: '8px 4px', textAlign: 'center', fontSize: '.72rem', fontWeight: 600,
            background: step === s ? 'var(--primary)' : i < ['scan','kyc','payment'].indexOf(step) ? '#d1fae5' : '#f9fafb',
            color: step === s ? '#fff' : i < ['scan','kyc','payment'].indexOf(step) ? '#065f46' : '#9ca3af',
          }}>
            {i < ['scan','kyc','payment'].indexOf(step) ? '✓ ' : ''}{label}
          </div>
        ))}
      </div>

      {/* ═══════════════ STEP 1: SCAN ════════════════ */}
      {step === 'scan' && (
        <div>
          <input ref={ocrInputRef} type="file" accept="image/*" capture="environment"
            style={{ display: 'none' }} onChange={handleOcrCapture} />

          {loading && <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>}

          {!loading && scanMode === 'idle' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button onClick={startCamera} style={btn('#4f46e5')}><Camera size={18} /> Scan STB Barcode</button>
              <button onClick={() => ocrInputRef.current?.click()} style={btn('#0d9488')}><ScanText size={18} /> Read NUID from TV Screen</button>
              <button onClick={() => setScanMode('manual')} style={btn('#0891b2')}><Search size={18} /> Enter NUID Manually</button>
            </div>
          )}

          {!loading && scanMode === 'camera' && (
            <div>
              <p style={{ color: '#555', fontSize: '.9rem', marginBottom: 10 }}>Point camera at STB barcode</p>
              <div id={SCANNER_ID} style={{ width: '100%', borderRadius: 10, overflow: 'hidden' }} />
              <button onClick={() => { stopCamera(); setScanMode('idle'); }} style={{ ...btn('#888'), marginTop: 12 }}>Cancel</button>
            </div>
          )}

          {!loading && scanMode === 'manual' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ fontWeight: 600, fontSize: '.9rem' }}>Enter NUID (from STB sticker or TV screen)</label>
              <input autoFocus value={manualSerial} onChange={e => setManual(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && lookupSerial(manualSerial)}
                placeholder="e.g. 3435522100" style={inputStyle} />
              {manualSerial && normalizeNUID(manualSerial) !== manualSerial.trim() && (
                <div style={{ fontSize: '.8rem', padding: '6px 10px', background: '#fef9c3', borderRadius: 6, border: '1px solid #fde047', color: '#713f12' }}>
                  Will use: <strong>{normalizeNUID(manualSerial)}</strong>
                </div>
              )}
              <button onClick={() => lookupSerial(manualSerial)} style={btn('#4f46e5')} disabled={!manualSerial}>
                <Search size={16} /> Find Customer
              </button>
            </div>
          )}

          {!loading && scanMode === 'ocr' && ocrProcessing && (
            <div style={{ textAlign: 'center', padding: 30 }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} />
              <p style={{ fontSize: '.9rem', color: '#555' }}>Reading numbers from TV screen...</p>
            </div>
          )}

          {!loading && scanMode === 'ocrResults' && (
            <div>
              <p style={{ fontWeight: 600, fontSize: '.9rem', marginBottom: 10 }}>Tap the NUID shown on TV screen:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {ocrCandidates.map(n => {
                  const norm = normalizeNUID(n);
                  return (
                    <button key={n} onClick={() => lookupSerial(n)} style={{ ...btn('#4f46e5'), flexDirection: 'column', alignItems: 'flex-start', padding: '12px 16px' }}>
                      <span style={{ fontSize: '1rem', fontFamily: 'monospace', letterSpacing: 1 }}>{n}</span>
                      {norm !== n && <span style={{ fontSize: '.72rem', opacity: .8, marginTop: 2 }}>→ {norm}</span>}
                    </button>
                  );
                })}
              </div>
              <button onClick={() => setScanMode('manual')} style={{ ...btn('#6b7280'), marginTop: 10 }}>Enter Manually Instead</button>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════ STEP 2: KYC ════════════════ */}
      {step === 'kyc' && customer && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Customer header */}
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>{customer.name}</h3>
              {customer.kyc?.status === 'verified'
                ? <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:'.78rem', color:'#16a34a', fontWeight:700 }}><ShieldCheck size={14}/>KYC Done</span>
                : <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:'.78rem', color:'#dc2626', fontWeight:700 }}><ShieldAlert size={14}/>KYC Pending</span>
              }
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:5, fontSize:'.83rem', color:'#444' }}>
              {/* Package / Plan */}
              {(customer.catv?.package || customer.broadband?.plan) && (
                <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 10px', background:'#f0f9ff', borderRadius:7 }}>
                  <span style={{ fontWeight:600, color:'#1d4ed8' }}>
                    {customer.catv?.package?.displayName || customer.catv?.package?.name
                      || customer.broadband?.plan?.displayName || customer.broadband?.plan?.name}
                  </span>
                  <span style={{ fontWeight:700, color:'#059669' }}>
                    ₹{customer.catv?.package?.price || customer.broadband?.plan?.price || '—'}/mo
                  </span>
                </div>
              )}
              {/* STB + Expiry */}
              <div style={{ display:'flex', gap:12 }}>
                <span><Tv size={11} style={{ verticalAlign:'middle', marginRight:3 }}/>STB: <strong>{customer.catv?.setTopBoxSerial || '—'}</strong></span>
              </div>
              {/* Due dates */}
              {customer.catv?.expiryDate && (() => {
                const days = Math.ceil((new Date(customer.catv.expiryDate) - new Date()) / 86400000);
                const color = days < 0 ? '#dc2626' : days <= 7 ? '#f97316' : '#16a34a';
                return (
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span><Calendar size={11} style={{ verticalAlign:'middle', marginRight:3 }}/>Due Date: <strong>{fmtDate(customer.catv.expiryDate)}</strong></span>
                    <span style={{ color, fontWeight:700, fontSize:'.78rem' }}>
                      {days < 0 ? `Expired ${Math.abs(days)}d ago` : days === 0 ? 'Expires today' : `${days}d left`}
                    </span>
                  </div>
                );
              })()}
              {customer.broadband?.expiryDate && (() => {
                const days = Math.ceil((new Date(customer.broadband.expiryDate) - new Date()) / 86400000);
                const color = days < 0 ? '#dc2626' : days <= 7 ? '#f97316' : '#16a34a';
                return (
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span><Calendar size={11} style={{ verticalAlign:'middle', marginRight:3 }}/>Due Date: <strong>{fmtDate(customer.broadband.expiryDate)}</strong></span>
                    <span style={{ color, fontWeight:700, fontSize:'.78rem' }}>
                      {days < 0 ? `Expired ${Math.abs(days)}d ago` : days === 0 ? 'Expires today' : `${days}d left`}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* KYC Form */}
          <div style={card}>
            <p style={{ margin: '0 0 12px', fontWeight: 700, fontSize: '.95rem' }}>Verify Customer Details</p>

            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>Full Name</label>
              <input value={kycForm.name} onChange={e => setKycForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
            </div>

            {/* Mobile + OTP */}
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>Mobile Number</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={kycForm.mobile} onChange={e => { setKycForm(f => ({ ...f, mobile: e.target.value })); setOtpSent(false); setOtpVerified(false); setDevOtp(null); }}
                  placeholder="10-digit mobile" style={{ ...inputStyle, flex: 1 }} maxLength={10} />
                {otpVerified
                  ? <span style={{ display:'flex', alignItems:'center', gap:4, color:'#16a34a', fontWeight:700, fontSize:'.82rem', whiteSpace:'nowrap' }}><Check size={16}/>Verified</span>
                  : <button onClick={sendOTP} disabled={otpLoading} style={{ ...btn('#0891b2'), padding:'10px 14px', whiteSpace:'nowrap', width:'auto' }}>
                      <Phone size={14}/>{otpLoading ? '...' : otpSent ? 'Resend' : 'Send OTP'}
                    </button>
                }
              </div>

              {/* Dev mode OTP hint */}
              {devOtp && (
                <div style={{ marginTop:6, padding:'10px 12px', background:'#fef9c3', borderRadius:8, border:'1px solid #fde047', color:'#713f12' }}>
                  <div style={{ fontSize:'.78rem', marginBottom:4 }}>
                    <KeyRound size={12} style={{ verticalAlign:'middle', marginRight:4 }}/>
                    SMS not delivered — show this OTP to customer:
                  </div>
                  <div style={{ fontSize:'1.6rem', fontWeight:800, letterSpacing:6, textAlign:'center', padding:'4px 0' }}>{devOtp}</div>
                </div>
              )}

              {otpSent && !otpVerified && (
                <div style={{ display:'flex', gap:8, marginTop:8 }}>
                  <input value={otpValue} onChange={e => setOtpValue(e.target.value)}
                    placeholder="Enter 6-digit OTP" style={{ ...inputStyle, flex:1, letterSpacing:4, fontSize:'1.1rem' }}
                    maxLength={6} onKeyDown={e => e.key === 'Enter' && verifyOTP()} />
                  <button onClick={verifyOTP} disabled={otpLoading} style={{ ...btn('#16a34a'), padding:'10px 14px', width:'auto' }}>
                    {otpLoading ? '...' : <><Check size={14}/> Verify</>}
                  </button>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>Address</label>
              <input value={kycForm.address} onChange={e => setKycForm(f => ({ ...f, address: e.target.value }))} style={inputStyle} />
            </div>
            <div style={{ display:'flex', gap:8, marginBottom:10 }}>
              <div style={{ flex:1 }}>
                <label style={lbl}>Area</label>
                <input value={kycForm.area} onChange={e => setKycForm(f => ({ ...f, area: e.target.value }))} style={inputStyle} />
              </div>
              <div style={{ flex:1 }}>
                <label style={lbl}>City</label>
                <input value={kycForm.city} onChange={e => setKycForm(f => ({ ...f, city: e.target.value }))} style={inputStyle} />
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>ID Proof</label>
              <div style={{ display:'flex', gap:8 }}>
                <select value={kycForm.idProofType} onChange={e => setKycForm(f => ({ ...f, idProofType: e.target.value }))}
                  style={{ ...inputStyle, flex:1 }}>
                  <option value="">Select type</option>
                  {ID_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input value={kycForm.idProofNumber} onChange={e => setKycForm(f => ({ ...f, idProofNumber: e.target.value }))}
                  placeholder="ID number" style={{ ...inputStyle, flex:1 }} />
              </div>
            </div>

            {/* Plan & Billing — only for CATV customers */}
            {(customer.customerType === 'catv' || customer.customerType === 'both') && (
              <div style={{ marginTop:14, paddingTop:14, borderTop:'1.5px dashed #e5e7eb' }}>
                <p style={{ margin:'0 0 10px', fontWeight:700, fontSize:'.88rem', color:'#374151' }}>Plan & Billing</p>

                <div style={{ marginBottom:10 }}>
                  <label style={lbl}>Package</label>
                  <select value={kycForm.catvPackageId}
                    onChange={e => {
                      const pkg = catvPackages.find(p => p._id === e.target.value);
                      setKycForm(f => ({ ...f, catvPackageId: e.target.value, customAmount: pkg ? String(pkg.price) : f.customAmount }));
                    }}
                    style={inputStyle}>
                    <option value="">— Select package —</option>
                    {catvPackages.map(p => (
                      <option key={p._id} value={p._id}>
                        {p.displayName || p.name} — ₹{p.price}/mo
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display:'flex', gap:8 }}>
                  <div style={{ flex:1 }}>
                    <label style={lbl}>Amount (₹)</label>
                    <input type="number" value={kycForm.customAmount}
                      onChange={e => setKycForm(f => ({ ...f, customAmount: e.target.value }))}
                      placeholder="400" style={{ ...inputStyle, fontWeight:700 }} />
                  </div>
                  <div style={{ flex:1 }}>
                    <label style={lbl}>Due Date</label>
                    <input type="date" value={kycForm.catvExpiryDate}
                      onChange={e => setKycForm(f => ({ ...f, catvExpiryDate: e.target.value }))}
                      style={inputStyle} />
                  </div>
                </div>
              </div>
            )}

            {/* Skip OTP option */}
            {!otpVerified && (
              <div style={{ marginTop:4 }}>
                <button onClick={() => setShowSkip(!showSkip)}
                  style={{ background:'none', border:'none', color:'#6b7280', fontSize:'.8rem', cursor:'pointer', padding:0, display:'flex', alignItems:'center', gap:4 }}>
                  <SkipForward size={13}/> Skip mobile verification
                </button>
                {showSkip && (
                  <div style={{ marginTop:8 }}>
                    <input value={skipReason} onChange={e => setSkipReason(e.target.value)}
                      placeholder="Reason (e.g. no mobile, not available)" style={{ ...inputStyle, fontSize:'.85rem' }} />
                  </div>
                )}
              </div>
            )}

            <button onClick={() => completeKYC(!otpVerified && showSkip)} disabled={kycSaving}
              style={{ ...btn('#4f46e5'), marginTop:16 }}>
              <ShieldCheck size={16}/>{kycSaving ? 'Saving...' : 'Complete KYC & Collect Payment'}
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════ STEP 3: PAYMENT ════════════════ */}
      {step === 'payment' && customer && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* KYC confirmed banner */}
          <div style={{ padding:'10px 14px', background:'#d1fae5', borderRadius:8, border:'1px solid #6ee7b7', display:'flex', alignItems:'center', gap:8, fontSize:'.88rem', color:'#065f46', fontWeight:600 }}>
            <ShieldCheck size={16}/> KYC Verified — {customer.name}
          </div>

          <div style={card}>
            <p style={{ margin:'0 0 12px', fontWeight:700, fontSize:'.95rem' }}>Collect Payment</p>

            <label style={lbl}>Plan Amount (₹)</label>
            <input type="number" value={baseAmount} onChange={e => setBaseAmount(e.target.value)}
              placeholder="Enter amount" style={{ ...inputStyle, fontSize:'1.2rem', fontWeight:700 }} />

            {discountPct > 0 && cashAmount > 0 && (
              <div style={{ display:'flex', gap:8, marginTop:10, padding:'8px 12px', background:'#f0fdf4', borderRadius:8, border:'1px solid #bbf7d0', fontSize:'.85rem' }}>
                <Tag size={14} color="#16a34a" style={{ flexShrink:0, marginTop:1 }}/>
                <span><strong style={{ color:'#16a34a' }}>{discountPct}% UPI discount</strong> — pays ₹{upiAmount} (saves ₹{cashAmount - upiAmount})</span>
              </div>
            )}

            <div style={{ display:'flex', gap:10, marginTop:14 }}>
              <button onClick={() => setPayMode(payMode === 'cash' ? null : 'cash')}
                style={{ ...btn(payMode === 'cash' ? '#15803d' : '#16a34a'), flex:1 }}>
                <Banknote size={16}/> Cash ₹{cashAmount || '—'}
              </button>
              <button onClick={() => setPayMode(payMode === 'upi' ? null : 'upi')}
                style={{ ...btn(payMode === 'upi' ? '#1d4ed8' : '#2563eb'), flex:1 }}>
                <CreditCard size={16}/> UPI ₹{upiAmount || '—'}
              </button>
            </div>

            {payMode === 'cash' && (
              <div style={{ marginTop:14, padding:14, background:'#f9fafb', borderRadius:10, textAlign:'center' }}>
                <p style={{ margin:'0 0 10px', color:'#555', fontSize:'.9rem' }}>
                  Collecting <strong>₹{cashAmount}</strong> cash from <strong>{customer.name}</strong>
                </p>
                <button onClick={() => collectPayment('cash')} disabled={paying} style={btn('#15803d')}>
                  {paying ? 'Recording...' : '✓ Confirm Cash Received'}
                </button>
              </div>
            )}

            {payMode === 'upi' && (
              <div style={{ marginTop:14 }}>
                {config.upiId2 && (
                  <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                    {[1,2].map(n => (
                      <button key={n} onClick={() => setSelUpi(n)}
                        style={{ flex:1, padding:8, borderRadius:8, border:`2px solid ${selectedUpi===n?'#2563eb':'#d1d5db'}`, background:selectedUpi===n?'#eff6ff':'#fff', cursor:'pointer', fontSize:'.78rem', fontWeight:600 }}>
                        {n===1?'HDFC Bank':'ICICI Bank'}<br/>
                        <span style={{ fontSize:'.7rem', fontWeight:400, color:'#666' }}>{n===1?config.upiId:config.upiId2}</span>
                      </button>
                    ))}
                  </div>
                )}
                {activeUpiId && (
                  <div style={{ textAlign:'center' }}>
                    {discountPct > 0 && (
                      <div style={{ marginBottom:10, padding:'6px 12px', background:'#f0fdf4', borderRadius:8, display:'inline-block', fontSize:'.85rem', color:'#15803d', fontWeight:600 }}>
                        🎉 {discountPct}% Discount — Customer pays ₹{upiAmount}
                      </div>
                    )}
                    <div style={{ background:'#fff', padding:16, borderRadius:12, display:'inline-block', border:'2px solid #e5e7eb', boxShadow:'0 2px 8px rgba(0,0,0,.08)' }}>
                      <QRCode value={makeUpiLink(activeUpiId, upiAmount, customer.customerId || customer._id)} size={200} />
                      <div style={{ marginTop:8, fontSize:'.75rem', color:'#888' }}>{config.upiName} · ₹{upiAmount} · UPI</div>
                    </div>
                    <div style={{ marginTop:12, padding:10, background:'#fffbeb', borderRadius:8, border:'1px solid #fde68a', fontSize:'.83rem', color:'#92400e' }}>
                      ⚠️ After customer pays, confirm below
                    </div>
                    <button onClick={() => collectPayment('upi')} disabled={paying} style={{ ...btn('#2563eb'), marginTop:10 }}>
                      {paying ? 'Recording...' : '✓ Customer Paid — Record It'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <button onClick={reset} style={{ ...btn('#6b7280'), padding:10 }}>← Next Customer</button>
        </div>
      )}
    </div>
  );
}

const btn = (bg) => ({ display:'flex', alignItems:'center', justifyContent:'center', gap:8, background:bg, color:'#fff', border:'none', borderRadius:10, padding:'13px 16px', fontSize:'.95rem', fontWeight:600, cursor:'pointer', width:'100%' });
const inputStyle = { width:'100%', padding:'10px 12px', border:'1.5px solid #d1d5db', borderRadius:8, fontSize:'.95rem', boxSizing:'border-box' };
const card = { background:'#fff', borderRadius:12, padding:16, boxShadow:'0 1px 8px rgba(0,0,0,.08)' };
const iconBtn = { marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'#888' };
const lbl = { fontSize:'.8rem', color:'#666', display:'block', marginBottom:3 };
